import { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { AdminAccessGuard } from "@/components/admin-access-guard";
import {
  GripVertical,
  EyeOff,
  Eye,
  Save,
  RotateCcw,
  PlusCircle,
  LayoutGrid,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { usePageTitle } from "@/hooks/use-page-title";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

const ALL_PANELS = [
  "notifications", "draftMessages", "messages", "waitingReplies",
  "supplyRequests", "locks", "approvals", "acquisitions", "renovations",
  "financials", "buyBoxReports", "thermostat", "maintenance", "occupancy", "listings",
];

const PANEL_LABELS: Record<string, string> = {
  notifications: "Notifications",
  draftMessages: "Draft Messages",
  messages: "Messages",
  waitingReplies: "Waiting Replies",
  supplyRequests: "Supply Requests",
  locks: "Lock Health",
  approvals: "Approvals",
  acquisitions: "Acquisitions",
  renovations: "Renovations",
  financials: "Financial Snapshot",
  buyBoxReports: "Buy Box Reports",
  thermostat: "Thermostat Health",
  maintenance: "Maintenance",
  occupancy: "Occupancy",
  listings: "Listing Reminders",
};

const TIER_DISPLAY: Record<string, { label: string; color: string }> = {
  Research: { label: "Research", color: "bg-purple-100 text-purple-800" },
  Host: { label: "Host", color: "bg-gray-100 text-gray-800" },
  Growth: { label: "Growth", color: "bg-blue-100 text-blue-800" },
  Professional: { label: "Professional", color: "bg-indigo-100 text-indigo-800" },
  Enterprise: { label: "Property Manager", color: "bg-orange-100 text-orange-800" },
  PMPro: { label: "PM Pro", color: "bg-amber-100 text-amber-800" },
  Founder25: { label: "Founder 25", color: "bg-yellow-100 text-yellow-800" },
  Founder50: { label: "Founder 50", color: "bg-yellow-100 text-yellow-800" },
  Founder100: { label: "Founder 100", color: "bg-yellow-100 text-yellow-800" },
  Interest: { label: "Interest", color: "bg-pink-100 text-pink-800" },
};

type TierDefault = {
  id?: number;
  tier: string;
  panelOrder: string[];
  panelSizes?: Record<string, string> | null;
  hiddenPanels?: string[] | null;
  updatedBy?: string | null;
  updatedAt?: string;
};

function SortablePanelRow({
  id,
  isHidden,
  onToggleHide,
}: {
  id: string;
  isHidden: boolean;
  onToggleHide: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: isHidden });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : isHidden ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-3 py-2 rounded-md border ${isHidden ? "bg-gray-50 border-dashed border-gray-300" : "bg-white border-gray-200"}`}
      data-testid={`tier-panel-row-${id}`}
    >
      {!isHidden && (
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          data-testid={`tier-drag-handle-${id}`}
        >
          <GripVertical className="h-4 w-4" />
        </div>
      )}
      {isHidden && <div className="w-4" />}
      <span className={`text-sm flex-1 ${isHidden ? "text-gray-400 line-through" : "text-gray-800"}`}>
        {PANEL_LABELS[id] ?? id}
      </span>
      <button
        type="button"
        onClick={() => onToggleHide(id)}
        className={`p-1 rounded hover:bg-gray-100 ${isHidden ? "text-gray-400" : "text-gray-500 hover:text-red-600"}`}
        title={isHidden ? "Show this card" : "Hide this card"}
        data-testid={`tier-toggle-hide-${id}`}
      >
        {isHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function TierEditor({ tierDefault, onSave }: {
  tierDefault: TierDefault;
  onSave: (tier: string, order: string[], hidden: string[]) => void;
}) {
  const { toast } = useToast();
  const [panelOrder, setPanelOrder] = useState<string[]>(() => {
    const saved = tierDefault.panelOrder ?? [];
    return [
      ...saved.filter((id) => ALL_PANELS.includes(id)),
      ...ALL_PANELS.filter((id) => !saved.includes(id)),
    ];
  });
  const [hiddenPanels, setHiddenPanels] = useState<string[]>(() =>
    (tierDefault.hiddenPanels ?? []).filter((id) => ALL_PANELS.includes(id))
  );
  const [isDirty, setIsDirty] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setPanelOrder((items) => {
        const oldIdx = items.indexOf(active.id as string);
        const newIdx = items.indexOf(over.id as string);
        return arrayMove(items, oldIdx, newIdx);
      });
      setIsDirty(true);
    }
  }

  function handleToggleHide(id: string) {
    setHiddenPanels((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setIsDirty(true);
  }

  function handleSave() {
    const visibleOrder = panelOrder.filter((id) => !hiddenPanels.includes(id));
    const fullOrder = [...visibleOrder, ...hiddenPanels];
    onSave(tierDefault.tier, fullOrder, hiddenPanels);
    setIsDirty(false);
  }

  function handleReset() {
    const saved = tierDefault.panelOrder ?? [];
    const validOrder = [
      ...saved.filter((id) => ALL_PANELS.includes(id)),
      ...ALL_PANELS.filter((id) => !saved.includes(id)),
    ];
    setPanelOrder(validOrder);
    setHiddenPanels((tierDefault.hiddenPanels ?? []).filter((id) => ALL_PANELS.includes(id)));
    setIsDirty(false);
  }

  const visibleOrder = panelOrder.filter((id) => !hiddenPanels.includes(id));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">Drag to reorder visible panels. Click the eye icon to hide/show a card.</p>
          {tierDefault.updatedAt && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Last updated {new Date(tierDefault.updatedAt).toLocaleDateString()}
              {tierDefault.updatedBy ? ` by ${tierDefault.updatedBy}` : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <Button variant="ghost" size="sm" onClick={handleReset} data-testid={`button-tier-reset-${tierDefault.tier}`}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Discard
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!isDirty}
            className="bg-brand hover:bg-brand-hover text-white"
            data-testid={`button-tier-save-${tierDefault.tier}`}
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            Save
          </Button>
        </div>
      </div>

      {/* Visible panels (drag to reorder) */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Visible panels ({visibleOrder.length})</p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleOrder} strategy={verticalListSortingStrategy}>
            <div className="space-y-1">
              {visibleOrder.map((id) => (
                <SortablePanelRow key={id} id={id} isHidden={false} onToggleHide={handleToggleHide} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Hidden panels */}
      {hiddenPanels.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hidden panels ({hiddenPanels.length})</p>
          <div className="space-y-1">
            {hiddenPanels.map((id) => (
              <SortablePanelRow key={id} id={id} isHidden={true} onToggleHide={handleToggleHide} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppAdminDashboardLayouts() {
  const queryClient = useQueryClient();
  usePageTitle("Dashboard Layouts");
  const { toast } = useToast();
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [savedTiers, setSavedTiers] = useState<Set<string>>(new Set());

  const defaultsQuery = useQuery<TierDefault[]>({
    queryKey: ["/api/app/admin/dashboard-tier-defaults"],
  });
  const defaults = defaultsQuery.data ?? [];

  const ALL_TIERS = [
    "Research", "Host", "Growth", "Professional", "Enterprise", "PMPro",
    "Founder25", "Founder50", "Founder100", "Interest",
  ];

  // Build a map from tier → data (use empty defaults for tiers without DB rows)
  const tierMap = new Map<string, TierDefault>(
    ALL_TIERS.map((t) => {
      const existing = defaults.find((d) => d.tier === t);
      return [t, existing ?? { tier: t, panelOrder: ALL_PANELS, hiddenPanels: null }];
    })
  );

  const saveMutation = useMutation({
    mutationFn: async ({ tier, panelOrder, hiddenPanels }: { tier: string; panelOrder: string[]; hiddenPanels: string[] }) => {
      await apiRequest("PUT", `/api/app/admin/dashboard-tier-defaults/${tier}`, { panelOrder, hiddenPanels: hiddenPanels.length > 0 ? hiddenPanels : null });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/dashboard-tier-defaults"] });
      setSavedTiers((prev) => new Set([...prev, variables.tier]));
      toast({ title: `${variables.tier} tier default saved` });
      setTimeout(() => {
        setSavedTiers((prev) => {
          const next = new Set(prev);
          next.delete(variables.tier);
          return next;
        });
      }, 2000);
    },
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  function handleSave(tier: string, panelOrder: string[], hiddenPanels: string[]) {
    saveMutation.mutate({ tier, panelOrder, hiddenPanels });
  }

  return (
    <AppLayout>
      <AdminAccessGuard error={defaultsQuery.error}>
      <div className="p-6 space-y-4 max-w-6xl" data-tour="admin-dashboard-layouts-section">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold" data-testid="heading-admin-dashboard-layouts">Dashboard Tier Defaults</h1>
            <p className="text-sm text-muted-foreground">
              Set the default panel order and visibility for each subscription tier. Users without saved preferences see this layout.
            </p>
          </div>
          <GuideButton pageKey="admin-dashboard-layouts" compactOnMobile />
        </div>
        <GuideNudgeBanner pageKey="admin-dashboard-layouts" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Tier list */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Tiers</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {defaultsQuery.isLoading ? (
                <div className="p-4 space-y-2">
                  {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : (
                <div className="divide-y">
                  {ALL_TIERS.map((tier) => {
                    const data = tierMap.get(tier);
                    const hiddenCount = (data?.hiddenPanels ?? []).length;
                    const tierInfo = TIER_DISPLAY[tier] ?? { label: tier, color: "bg-gray-100 text-gray-800" };
                    return (
                      <button
                        key={tier}
                        onClick={() => setSelectedTier(tier === selectedTier ? null : tier)}
                        className={`w-full text-left px-4 py-3 hover:bg-accent transition-colors ${selectedTier === tier ? "bg-accent" : ""}`}
                        data-testid={`tier-row-${tier}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{tierInfo.label}</span>
                              {savedTiers.has(tier) && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
                            </div>
                            {hiddenCount > 0 && (
                              <p className="text-xs text-muted-foreground">{hiddenCount} panel{hiddenCount > 1 ? "s" : ""} hidden</p>
                            )}
                          </div>
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${tierInfo.color}`}>
                            {tierInfo.label}
                          </Badge>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Editor panel */}
          <Card className="lg:col-span-2">
            {!selectedTier ? (
              <CardContent className="flex items-center justify-center h-40 text-sm text-muted-foreground">
                <div className="text-center">
                  <LayoutGrid className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                  Select a tier to edit its default dashboard layout.
                </div>
              </CardContent>
            ) : (
              <>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{TIER_DISPLAY[selectedTier]?.label ?? selectedTier} Tier</CardTitle>
                  <CardDescription>
                    Default dashboard layout for users on the {TIER_DISPLAY[selectedTier]?.label ?? selectedTier} plan who have not saved their own preferences.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <TierEditor
                    key={selectedTier}
                    tierDefault={tierMap.get(selectedTier)!}
                    onSave={handleSave}
                  />
                </CardContent>
              </>
            )}
          </Card>
        </div>

        {selectedTier && (
          <Card data-testid={`card-tier-preview-${selectedTier}`}>
            <CardHeader className="pb-3 flex flex-row items-start justify-between gap-2 space-y-0">
              <div>
                <CardTitle className="text-base">Preview: {TIER_DISPLAY[selectedTier]?.label ?? selectedTier} dashboard</CardTitle>
                <CardDescription>
                  Read-only preview of how the dashboard renders for a host on this tier with no saved preferences. Reflects the most recently saved tier default.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                asChild
                data-testid={`button-tier-preview-open-${selectedTier}`}
              >
                <a
                  href={`/app/dashboard?previewTier=${encodeURIComponent(selectedTier)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Open in new tab
                </a>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border bg-white overflow-hidden">
                <iframe
                  key={selectedTier}
                  title={`Dashboard preview for ${selectedTier}`}
                  src={`/app/dashboard?previewTier=${encodeURIComponent(selectedTier)}`}
                  className="w-full h-[800px] border-0"
                  data-testid={`iframe-tier-preview-${selectedTier}`}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      </AdminAccessGuard>
    </AppLayout>
  );
}
